import React, { useState, useEffect } from 'react';
import Layout from '../../components/ui/Layout';
import SubHeader from '../../components/ui/SubHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PropertySidebar from './components/PropertySidebar';
import PropertyDetailsTab from './components/PropertyDetailsTab';
import LocationTab from './components/LocationTab';
import MediaTab from './components/MediaTab';
import OwnerTab from './components/OwnerTab';
import PricingTab from './components/PricingTab';
import BulkImportModal from './components/BulkImportModal';
import FieldManagementModal from '../property-management-dashboard/components/FieldManagementModal';
import { getCustomFields, saveCustomFields } from '../../utils/customFieldsManager';
import DocumentAttachment from '../../components/ui/DocumentAttachment';
import ActionBoxes from '../../components/ui/ActionBoxes';



const PropertyListingCreationAndManagement = () => {
  const [activeTab, setActiveTab] = useState('details');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [showFieldManagement, setShowFieldManagement] = useState(false);
  const [isQuickMenuOpen, setIsQuickMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    propertyId: 'PROP-2026-001',
    title: '',
    propertyType: '',
    purpose: '',
    bedrooms: '',
    bathrooms: '',
    area: '',
    floor: '',
    furnishing: '',
    yearBuilt: '',
    propertyAge: '',
    description: '',
    features: [],
    notes: '',
    address: '',
    city: '',
    neighborhood: '',
    postalCode: '',
    latitude: '24.7136',
    longitude: '46.6753',
    locationNotes: '',
    images: [],
    videos: [],
    documents: [],
    ownerName: '',
    ownerType: '',
    ownerIdNumber: '',
    ownerPhone: '',
    ownerEmail: '',
    ownerAlternatePhone: '',
    ownerAddress: '',
    ownerNotes: '',
    listingBroughtBy: '',
    listingSource: '',
    dealingWith: '',
    ownerApprovalStatus: '',
    price: '',
    currency: 'JOD',
    commission: '',
    maintenanceFee: '',
    securityDeposit: '',
    paymentTerms: '',
    paymentOptions: {},
    negotiable: false,
    pricingNotes: '',
    priceRequested: '',
    status: 'draft',
    customFields: {}
  });
  const [errors, setErrors] = useState({});

  const mockProperties = [
    {
      id: 1,
      propertyId: 'PROP-2026-001',
      title: 'فيلا فاخرة في حي عبدون',
      city: 'عمان',
      price: '2,500,000 د.أ',
      status: 'active'
    },
    {
      id: 2,
      propertyId: 'PROP-2026-002',
      title: 'شقة عصرية في خلدا',
      city: 'عمان',
      price: '850,000 د.أ',
      status: 'pending'
    },
    {
      id: 3,
      propertyId: 'PROP-2026-003',
      title: 'مكتب تجاري في الشميساني',
      city: 'عمان',
      price: '1,200,000 د.أ',
      status: 'active'
    },
    {
      id: 4,
      propertyId: 'PROP-2026-004',
      title: 'أرض سكنية في ماركا',
      city: 'عمان',
      price: '750,000 د.أ',
      status: 'draft'
    },
    {
      id: 5,
      propertyId: 'PROP-2026-005',
      title: 'محل تجاري في جبل عمان',
      city: 'عمان',
      price: '450,000 د.أ',
      status: 'active'
    }
  ];

  const [properties] = useState(mockProperties);

  const breadcrumbItems = [
    { label: 'الرئيسية', path: '/property-management-dashboard', icon: 'Home' },
    { label: 'إدارة العقارات', path: '/property-listing-creation-and-management' }
  ];

  const quickActions = [
    {
      label: 'إدارة الحقول',
      icon: 'Settings',
      onClick: () => setShowFieldManagement(true),
      roles: ['admin', 'property_manager'],
      variant: 'default'
    },
    {
      label: 'استيراد من Excel',
      icon: 'FileSpreadsheet',
      onClick: () => setShowBulkImport(true),
      roles: ['admin', 'property_manager'],
      variant: 'default'
    },
    {
      label: 'طباعة',
      icon: 'Printer',
      onClick: handlePrint,
      roles: ['admin', 'property_manager'],
      variant: 'default'
    },
    {
      label: 'تصدير PDF',
      icon: 'FileText',
      onClick: handleExportPDF,
      roles: ['admin', 'property_manager'],
      variant: 'default'
    }
  ];

  const handleQuickMenuToggle = () => {
    setIsQuickMenuOpen(!isQuickMenuOpen);
  };

  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      handleAutoSave();
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [formData]);

  useEffect(() => {
    const loadedFields = getCustomFields();
    setCustomFields(loadedFields);
  }, []);

  const handleAutoSave = () => {
    if (formData?.title || formData?.description) {
      setIsSaving(true);
      setTimeout(() => {
        setLastSaved(new Date());
        setIsSaving(false);
      }, 1000);
    }
  };

  const handleFormChange = (field, value) => {
    if (field?.startsWith('customFields.')) {
      const customFieldId = field?.replace('customFields.', '');
      setFormData(prev => ({
        ...prev,
        customFields: {
          ...prev?.customFields,
          [customFieldId]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSaveCustomFields = (fields) => {
    saveCustomFields(fields);
    setCustomFields(fields);
  };

  const validateForm = () => {
    const newErrors = {};

    if (activeTab === 'details') {
      if (!formData?.title) newErrors.title = 'عنوان العقار مطلوب';
      if (!formData?.propertyType) newErrors.propertyType = 'نوع العقار مطلوب';
      if (!formData?.purpose) newErrors.purpose = 'الغرض مطلوب';
      if (!formData?.area) newErrors.area = 'المساحة مطلوبة';
      if (!formData?.description) newErrors.description = 'الوصف مطلوب';
    }

    if (activeTab === 'location') {
      if (!formData?.address) newErrors.address = 'العنوان مطلوب';
      if (!formData?.city) newErrors.city = 'المدينة مطلوبة';
      if (!formData?.neighborhood) newErrors.neighborhood = 'الحي مطلوب';
    }

    if (activeTab === 'owner') {
      if (!formData?.ownerName) newErrors.ownerName = 'اسم المالك مطلوب';
      if (!formData?.ownerType) newErrors.ownerType = 'نوع المالك مطلوب';
      if (!formData?.ownerIdNumber) newErrors.ownerIdNumber = 'رقم الهوية مطلوب';
      if (!formData?.ownerPhone) newErrors.ownerPhone = 'رقم الجوال مطلوب';
    }

    if (activeTab === 'pricing') {
      if (!formData?.price) newErrors.price = 'السعر مطلوب';
      if (!formData?.currency) newErrors.currency = 'العملة مطلوبة';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      setIsSaving(true);
      setTimeout(() => {
        setLastSaved(new Date());
        setIsSaving(false);
        console.log('Property saved:', formData);
      }, 1500);
    }
  };

  const handlePublish = () => {
    if (validateForm()) {
      setFormData(prev => ({ ...prev, status: 'active' }));
      console.log('Property published:', formData);
    }
  };

  function handlePrint() {
    window.print();
  }

  function handleExportPDF() {
    console.log('Exporting to PDF:', formData);
  }

  const handleAddNew = () => {
    setFormData({
      propertyId: `PROP-2026-${String(properties?.length + 1)?.padStart(3, '0')}`,
      title: '',
      propertyType: '',
      purpose: '',
      bedrooms: '',
      bathrooms: '',
      area: '',
      floor: '',
      furnishing: '',
      yearBuilt: '',
      propertyAge: '',
      description: '',
      features: [],
      notes: '',
      address: '',
      city: '',
      neighborhood: '',
      postalCode: '',
      latitude: '24.7136',
      longitude: '46.6753',
      locationNotes: '',
      images: [],
      videos: [],
      documents: [],
      ownerName: '',
      ownerType: '',
      ownerIdNumber: '',
      ownerPhone: '',
      ownerEmail: '',
      ownerAlternatePhone: '',
      ownerAddress: '',
      ownerNotes: '',
      listingBroughtBy: '',
      listingSource: '',
      dealingWith: '',
      ownerApprovalStatus: '',
      price: '',
      currency: 'JOD',
      commission: '',
      maintenanceFee: '',
      securityDeposit: '',
      paymentTerms: '',
      paymentOptions: {},
      negotiable: false,
      pricingNotes: '',
      priceRequested: '',
      status: 'draft',
      customFields: {}
    });
    setSelectedProperty(null);
    setActiveTab('details');
    setErrors({});
  };

  const handleBulkImport = (file) => {
    console.log('Importing file:', file?.name);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return <PropertyDetailsTab formData={formData} onChange={handleFormChange} errors={errors} customFields={customFields} />;
      case 'location':
        return <LocationTab formData={formData} onChange={handleFormChange} errors={errors} customFields={customFields} />;
      case 'media':
        return <MediaTab formData={formData} onChange={handleFormChange} />;
      case 'documents':
        return (
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="mb-6">
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">
                المستندات والملفات
              </h2>
              <p className="text-sm text-muted-foreground">
                قم برفع المستندات والملفات المتعلقة بالعقار مثل العقود، الوثائق، والتقارير
              </p>
            </div>
            <DocumentAttachment
              documents={formData?.documents}
              onChange={(docs) => handleFormChange('documents', docs)}
              entityType="property"
              entityId={formData?.propertyId}
            />
          </div>
        );
      case 'owner':
        return <OwnerTab formData={formData} onChange={handleFormChange} errors={errors} customFields={customFields} />;
      case 'pricing':
        return <PricingTab formData={formData} onChange={handleFormChange} errors={errors} customFields={customFields} />;
      default:
        return null;
    }
  };

  return (
    <Layout 
      userRole="property_manager" 
      userName="أحمد محمد"
      onQuickMenuToggle={handleQuickMenuToggle}
    >
      <div className="space-y-4 md:space-y-6">
        <SubHeader pageTitle="قوائم العقارات" />
        <ActionBoxes 
          actions={[
            {
              id: 'field-management',
              icon: 'Settings',
              label: 'إدارة الحقول',
              color: '#3B82F6',
              bgGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(96, 165, 250, 0.25) 100%)',
              borderColor: 'rgba(59, 130, 246, 0.5)',
              onClick: () => setShowFieldManagement(true)
            },
            {
              id: 'excel-export',
              icon: 'FileSpreadsheet',
              label: 'تصدير Excel',
              color: '#10B981',
              bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.25) 100%)',
              borderColor: 'rgba(16, 185, 129, 0.5)',
              onClick: () => console.log('Excel Export')
            },
            {
              id: 'bulk-import',
              icon: 'Upload',
              label: 'استيراد جماعي',
              color: '#8B5CF6',
              bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(167, 139, 250, 0.25) 100%)',
              borderColor: 'rgba(139, 92, 246, 0.5)',
              onClick: () => setShowBulkImport(true)
            },
            {
              id: 'add-property',
              icon: 'Plus',
              label: 'إضافة عقار',
              color: '#F59E0B',
              bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(251, 191, 36, 0.25) 100%)',
              borderColor: 'rgba(245, 158, 11, 0.5)',
              onClick: handleAddNew
            }
          ]} 
          showNewContractModal={false}
          setShowNewContractModal={() => {}}
        />
        
        <div className="flex flex-col lg:flex-row gap-4 md:gap-6 h-[calc(100vh-200px)]">
          <PropertySidebar
            properties={properties}
            selectedProperty={selectedProperty}
            onSelectProperty={setSelectedProperty}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAddNew={handleAddNew}
          />

          <div className="flex-1 bg-card rounded-lg border border-border shadow-warm overflow-hidden flex flex-col">
            <div className="p-4 md:p-6 border-b border-border">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
                    {formData?.title || 'عقار جديد'}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1 font-data">
                    {formData?.propertyId}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {isSaving && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Loader2" size={16} className="animate-spin" />
                      <span>جاري الحفظ...</span>
                    </div>
                  )}
                  {lastSaved && !isSaving && (
                    <div className="flex items-center gap-2 text-sm text-success">
                      <Icon name="Check" size={16} />
                      <span>تم الحفظ</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {renderTabContent()}
            </div>

            <div className="p-4 md:p-6 border-t border-border bg-muted/50">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-sm text-muted-foreground">
                  {lastSaved && (
                    <span>آخر حفظ: {lastSaved?.toLocaleTimeString('ar-SA')}</span>
                  )}
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    onClick={handleSave}
                    loading={isSaving}
                    iconName="Save"
                    iconPosition="left"
                    className="flex-1 sm:flex-initial"
                  >
                    حفظ
                  </Button>
                  <Button
                    variant="default"
                    onClick={handlePublish}
                    iconName="Send"
                    iconPosition="left"
                    className="flex-1 sm:flex-initial"
                  >
                    نشر العقار
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImport={handleBulkImport}
      />
      <FieldManagementModal
        isOpen={showFieldManagement}
        onClose={() => setShowFieldManagement(false)}
        onSave={handleSaveCustomFields}
        existingFields={customFields}
      />
    </Layout>
  );
};

export default PropertyListingCreationAndManagement;